<script setup lang="ts">
import { ErrorState, EmptyState, KindIcon, NavIcon, Skeleton } from '@taler/ui'
import { equalsLocalDate, formatDate, formatMoney, type LocalDate } from '@taler/domain'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { groupByDay, type DayGroup, type KindSubtotal } from '../internal/day-group.ts'
import type { TransactionRow } from '../internal/transaction-row.ts'
import type { CrossLedgerLink, CrossLedgerLinksByEntryId } from '../composables/use-cross-ledger-links.ts'
import TransactionRowWide from '@/features/transactions/components/TransactionRowWide.vue'

const props = defineProps<{
  rows: readonly TransactionRow[]
  crossLedgerLinks?: CrossLedgerLinksByEntryId
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  retrying?: boolean
  grouped: boolean
  filtersActive: boolean
  canEdit: boolean
  canReverse: boolean
}>()

const emit = defineEmits<{
  loadMore: []
  retry: []
  edit: [string]
  reverse: [string]
  followCrossLedgerLink: [CrossLedgerLink]
}>()

const { t, locale } = useI18n()

/** Flat table only: a run of same-day rows prints the date once. Grouped rows always print their own. */
const dateVisibility = computed(() =>
  props.rows.map((row, index) => index === 0 || !equalsLocalDate(row.date, props.rows[index - 1]!.date)),
)

const groups = computed(() => groupByDay(props.rows, props.hasNextPage))

/** Global position of a row across every group, so the keyset-arrival fade keeps
 * animating rows in load order regardless of which day's group they land in. */
const rowIndex = computed(() => {
  const map = new Map<string, number>()
  props.rows.forEach((row, index) => map.set(row.id, index))
  return map
})

// A day's per-kind subtotal is a chip: the kind's colour on a tint of itself, with the kind icon.
const SUBTOTAL_TONE_CLASS: Readonly<Record<KindSubtotal['kind'], string>> = {
  INCOME:
    'text-[color:var(--color-income)] bg-[color:color-mix(in_srgb,var(--color-income)_12%,transparent)]',
  EXPENSE:
    'text-[color:var(--color-expense)] bg-[color:color-mix(in_srgb,var(--color-expense)_12%,transparent)]',
  TRANSFER:
    'text-[color:var(--color-transfer)] bg-[color:color-mix(in_srgb,var(--color-transfer)_12%,transparent)]',
}

function groupDateLabel(date: LocalDate): string {
  return formatDate(date, locale.value as 'en' | 'hu', 'long')
}

function entryCountLabel(group: DayGroup): string {
  if (group.isPartial) return t('transactions.groupEntryCountPartial', { count: group.rows.length })
  return group.rows.length === 1
    ? t('transactions.groupEntryCountOne')
    : t('transactions.groupEntryCount', { count: group.rows.length })
}

function kindLabel(kind: KindSubtotal['kind']): string {
  return t(`transactions.${kind.toLowerCase()}`)
}

function subtotalText(subtotal: KindSubtotal): string {
  return formatMoney(subtotal.total, {
    locale: locale.value as 'en' | 'hu',
    sign: subtotal.kind !== 'TRANSFER',
  })
}

/**
 * The row-arrival fade is for a keyset page landing, not the page's own first paint - a
 * page settling into view does not need to fade its own content in, and doing so would
 * leave a moment where a screen reader or an accessibility audit catches a row still
 * mid-transition. So only rows added after the initial load animate in.
 */
const settledCount = ref<number | undefined>(undefined)
watch(
  () => props.isLoading,
  (loading) => {
    if (!loading && settledCount.value === undefined) settledCount.value = props.rows.length
  },
  { immediate: true },
)
</script>

<template>
  <div>
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-3 px-3 py-2">
      <li v-for="index in 6" :key="index" class="flex items-center gap-3">
        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton width="60%" height="1rem" />
          <Skeleton width="40%" height="0.75rem" />
        </div>
        <Skeleton width="4.5rem" height="1.25rem" />
      </li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('transactions.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('transactions.errorTitle')"
      :body="t('transactions.errorBody')"
      :retry-label="t('transactions.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="rows.length === 0"
      :title="filtersActive ? t('transactions.emptyFilteredTitle') : t('transactions.emptyTitle')"
      :body="filtersActive ? t('transactions.emptyFilteredBody') : t('transactions.emptyBody')"
    >
      <template #icon><NavIcon name="transactions" /></template>
    </EmptyState>

    <template v-else>
      <div
        class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
        tabindex="0"
        role="region"
        :aria-label="t('transactions.tableRegion')"
      >
        <table class="w-full border-collapse bg-[color:var(--color-surface)]">
          <thead>
            <tr class="table-head">
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                {{ t('transactions.date') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                {{ t('transactions.entry') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                {{ t('transactions.summary') }}
              </th>
              <th class="px-[var(--space-cell)] py-[var(--space-inline)] text-right" scope="col">
                {{ t('transactions.effects') }}
              </th>
              <th class="w-px px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
                <span class="sr-only">{{ t('transactions.actions') }}</span>
              </th>
            </tr>
          </thead>
          <template v-if="!grouped">
            <tbody>
              <TransactionRowWide
                v-for="(row, index) in rows"
                :key="row.id"
                :row="row"
                :show-date="dateVisibility[index]!"
                :can-edit="canEdit"
                :can-reverse="canReverse"
                :cross-ledger-link="crossLedgerLinks?.get(row.id)"
                @edit="emit('edit', $event)"
                @reverse="emit('reverse', $event)"
                @follow-cross-ledger-link="emit('followCrossLedgerLink', $event)"
                :arrive="settledCount !== undefined && index >= settledCount"
                :style="
                  settledCount !== undefined && index >= settledCount
                    ? { '--row-arrive-index': index - settledCount }
                    : undefined
                "
              />
            </tbody>
          </template>
          <template v-else>
            <tbody v-for="group in groups" :key="group.date.epochDay">
              <tr class="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)]">
                <th
                  colspan="5"
                  scope="rowgroup"
                  class="px-[var(--space-cell)] py-[var(--space-inline)] text-left"
                >
                  <div class="flex items-center gap-[var(--space-cell)]">
                    <span
                      class="text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text)]"
                    >
                      {{ groupDateLabel(group.date) }}
                    </span>
                    <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                      {{ entryCountLabel(group) }}
                    </span>
                    <span
                      v-if="group.isPartial"
                      class="text-[length:var(--text-step-1)] italic text-[color:var(--color-text-muted)]"
                    >
                      {{ t('transactions.groupSubtotalsPending') }}
                    </span>
                    <span
                      v-else
                      class="ml-auto flex flex-wrap items-center justify-end gap-[var(--space-inline)]"
                    >
                      <span
                        v-for="subtotal in group.subtotals"
                        :key="`${subtotal.kind}:${subtotal.currency}`"
                        class="flex items-center gap-[var(--space-tight)] rounded-full py-px pl-[var(--space-tight)] pr-[var(--space-inline)] text-[length:var(--text-step-1)] font-semibold tabular-nums"
                        :class="SUBTOTAL_TONE_CLASS[subtotal.kind]"
                        :title="
                          subtotal.kind === 'TRANSFER' ? t('transactions.transferSubtotalHint') : undefined
                        "
                      >
                        <KindIcon :kind="subtotal.kind" :label="kindLabel(subtotal.kind)" />
                        {{ subtotalText(subtotal) }}
                      </span>
                    </span>
                  </div>
                </th>
              </tr>
              <TransactionRowWide
                v-for="row in group.rows"
                :key="row.id"
                :row="row"
                :show-date="true"
                :can-edit="canEdit"
                :can-reverse="canReverse"
                :cross-ledger-link="crossLedgerLinks?.get(row.id)"
                @edit="emit('edit', $event)"
                @reverse="emit('reverse', $event)"
                @follow-cross-ledger-link="emit('followCrossLedgerLink', $event)"
                :arrive="settledCount !== undefined && rowIndex.get(row.id)! >= settledCount"
                :style="
                  settledCount !== undefined && rowIndex.get(row.id)! >= settledCount
                    ? { '--row-arrive-index': rowIndex.get(row.id)! - settledCount }
                    : undefined
                "
              />
            </tbody>
          </template>
        </table>
      </div>

      <ul v-if="isFetchingNextPage" aria-hidden="true" class="flex flex-col gap-3 px-3 py-2">
        <li v-for="index in 3" :key="index" class="flex items-center gap-3">
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton width="55%" height="1rem" />
            <Skeleton width="35%" height="0.75rem" />
          </div>
          <Skeleton width="4.5rem" height="1.25rem" />
        </li>
      </ul>

      <div class="flex justify-center p-4">
        <button
          v-if="hasNextPage"
          type="button"
          :disabled="isFetchingNextPage"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)] disabled:opacity-60"
          @click="emit('loadMore')"
        >
          {{ isFetchingNextPage ? t('transactions.loadingMore') : t('transactions.loadMore') }}
        </button>
        <p v-else class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('transactions.endOfList') }}
        </p>
      </div>
    </template>
  </div>
</template>
